// file: client/src/writer/components/ChapterContentEditor.tsx
import React from 'react';
import { EditorContent, Editor } from '@tiptap/react';
import { Chapter, ChapterVersion, LoadingStates } from '../types';
import { labelClass, inputClass, textareaClass, actionButtonClass, primaryButtonClass, secondaryButtonClass } from '../constants';

interface ChapterContentEditorProps {
    activeChapter: Chapter;
    activeVersion: ChapterVersion;
    editor: Editor | null;
    revisionInput: string;
    onRevisionInputChange: (value: string) => void;
    revisionWordCount: string;
    onRevisionWordCountChange: (value: string) => void;
    onReviseText: () => void;
    onNavigateVersion: (direction: 'prev' | 'next') => void;
    onSetChapterStatus: (status: Chapter['status']) => void;
    isLoading: LoadingStates;
}

const ChapterContentEditor: React.FC<ChapterContentEditorProps> = ({
    activeChapter, activeVersion, editor, revisionInput, onRevisionInputChange,
    revisionWordCount, onRevisionWordCountChange, onReviseText,
    onNavigateVersion, onSetChapterStatus, isLoading
}) => {
    return (
        <div className="space-y-4 h-full flex flex-col">
            <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Version {(activeChapter.historyIndex ?? 0) + 1} of {(activeChapter.history ?? []).length}</p>
                <div className="flex space-x-2">
                    <button onClick={() => onNavigateVersion('prev')} disabled={(activeChapter.historyIndex ?? 0) <= 0} className={secondaryButtonClass}>Prev. Version</button>
                    <button onClick={() => onNavigateVersion('next')} disabled={(activeChapter.historyIndex ?? 0) >= (activeChapter.history ?? []).length - 1} className={secondaryButtonClass}>Next Version</button>
                </div>
            </div>

            <div>
                <h4 className="font-semibold text-gray-700 mb-1">AI Generated Summary for this Version:</h4>
                <p className="text-sm p-3 bg-gray-50 rounded-md border border-gray-200 min-h-[60px]">{activeVersion.generatedSummary}</p>
                {activeVersion.revisionNotes && <p className="text-xs italic text-gray-500 mt-1">Revision based on: "{activeVersion.revisionNotes}"</p>}
            </div>

            <div className="flex-grow flex flex-col min-h-[250px]">
                <h4 className="font-semibold text-gray-700 mb-1">Chapter Content (Version {(activeChapter.historyIndex ?? 0) + 1}):</h4>
                <div className="prose max-w-none border border-gray-300 rounded-md p-1 flex-grow overflow-y-auto">
                    <EditorContent editor={editor} className="h-full" />
                </div>
            </div>
            
            <div className="mt-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-700 mb-2">Revise This Version:</h4>
                <div><label className={labelClass} htmlFor="revInstr">Revision Instructions:</label><textarea id="revInstr" value={revisionInput} onChange={e => onRevisionInputChange(e.target.value)} placeholder="e.g., Make the dialogue more tense, add more description of the forest..." className={textareaClass}></textarea></div>
                <div className="mt-2"><label className={labelClass} htmlFor="revWords">Expected Word Count (for revision):</label><input id="revWords" type="number" value={revisionWordCount} onChange={e => onRevisionWordCountChange(e.target.value)} className={inputClass} /></div>
                <button onClick={onReviseText} disabled={isLoading['reviseText']} className={`${primaryButtonClass} w-full mt-2`}>
                    {isLoading['reviseText'] ? 'Revising...' : 'Revise This Version'}
                </button>
            </div>

            <div className="mt-4 pt-4 border-t">
                <h4 className="font-semibold text-gray-700 mb-2">Chapter Status: <span className="font-normal bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs">{activeChapter.status}</span></h4>
                <div className="flex space-x-2">
                    <button onClick={() => onSetChapterStatus('draft')} className={activeChapter.status === 'draft' ? `${actionButtonClass} opacity-50` : secondaryButtonClass}>Set as Draft</button>
                    <button onClick={() => onSetChapterStatus('reviewed')} className={activeChapter.status === 'reviewed' ? `${actionButtonClass} opacity-50` : secondaryButtonClass}>Set as Reviewed</button>
                    <button onClick={() => onSetChapterStatus('finalized')} className={activeChapter.status === 'finalized' ? `${actionButtonClass} opacity-50` : secondaryButtonClass}>Set as Finalized</button>
                </div>
            </div>
        </div>
    );
};

export default ChapterContentEditor;

